import { flow, types } from 'mobx-state-tree';
import { get } from 'lodash';
import moment from 'moment';

import { getDependencies } from '../../util/get-dependencies';
import { getGiftCard } from '../../helpers/get-gift-card';
import AlternativePaymentMethod from '../alternative-payment-method';
import CreditCard from '../credit-card';
import GiftCard from '../gift-card';
import StoredPaymentMethod from '../stored-payment-method';

const Payment = types
  .model('Payment', {
    errorMessage: types.maybe(types.string),
    storePaymentMethod: false,
    alternativePaymentMethod: types.maybe(AlternativePaymentMethod),
    creditCard: types.maybe(CreditCard),
    giftCard: types.maybe(GiftCard),
    paymentMethod: types.maybe(types.reference(StoredPaymentMethod)),
    useNewCreditCard: false,
  })
  .actions(self => ({
    setAlternativePaymentMethod(alternativePaymentMethod) {
      self.alternativePaymentMethod = alternativePaymentMethod;
    },
    setPaymentMethod(paymentMethod) {
      self.paymentMethod = paymentMethod;
    },
    setStorePaymentMethod(storePaymentMethod) {
      self.storePaymentMethod = storePaymentMethod;
    },
    setCreditCard(creditCard) {
      self.creditCard = creditCard;
    },
    setUseNewCreditCard(shouldUse) {
      self.useNewCreditCard = shouldUse;
    },
    setErrorMessage(message) {
      self.errorMessage = message;
    },
    setGiftCard: flow(function*(cardNumber, verificationCode) {
      const { getApiClient } = getDependencies(self);
      const apiClient = yield getApiClient();
      const giftCard = yield getGiftCard(cardNumber, verificationCode, { apiClient });

      if (giftCard === null) {
        return false;
      }

      self.giftCard = {
        balance: giftCard.balance,
        cardNumber: giftCard.cardNumber,
        expiryDate: giftCard.expiryDate ? moment(giftCard.expiryDate).toDate() : null,
        verificationCode,
      };

      return true;
    }),
    checkGiftCard: flow(function*(cardNumber, verificationCode) {
      const { getApiClient } = getDependencies(self);
      const apiClient = yield getApiClient();
      const giftCard = yield getGiftCard(cardNumber, verificationCode, { apiClient });

      if (giftCard === null) {
        return {
          success: false,
          error: 'The gift card details you entered are invalid, please check them and try again.',
        };
      }

      return {
        success: true,
        balance: giftCard.balance,
        cardNumber: giftCard.cardNumber,
        expiryDate: giftCard.expiryDate ? moment(giftCard.expiryDate).toDate() : null,
        verificationCode,
      };
    }),

    removeGiftCard() {
      self.giftCard = undefined;
    },
  }))
  .views(self => ({
    get browserInfo() {
      const isCreditCardOrStoredPayment = self.creditCard || self.paymentMethod;
      if (!isCreditCardOrStoredPayment) {
        return null;
      }
      return self.isStoredPayment ? get(self, 'paymentMethod.browserInfo') : get(self, 'creditCard.browserInfo');
    },
    get isPayingWithAlternativePaymentMethod() {
      return Boolean(get(self, 'alternativePaymentMethod.token'));
    },
    get isStoredPayment() {
      return !self.useNewCreditCard;
    },
    get hasCreditCard() {
      return !!self.creditCard;
    },
    get paymentMethodId() {
      if (!self.paymentMethod) {
        return null;
      }
      return self.paymentMethod.id;
    },
  }));

export default Payment;
